import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { BE_API_AUTH_URL } from '../constants/app.constants';

export interface HospitalityAiCompanyOption {
  id: string;
  name: string;
  isActive?: boolean;
  properties: Array<{ id: string; address: string }>;
}

export interface HospitalityAiContext {
  companies: HospitalityAiCompanyOption[];
  languages: Array<{ code: string }>;
}

export interface HospitalityAiVoteTrendEntry {
  date: string;
  up: number;
  down: number;
}

export interface HospitalityAiTotals {
  turns: number;
  votes: number;
  helpfulRate: number | null;
  votesByLanguage: Record<string, { up: number; down: number }>;
  voteSummary: { up: number; down: number };
  voteTrend: HospitalityAiVoteTrendEntry[];
  downvotesLastSevenDays: number;
  qaEntries: { active: number; inactive: number; total: number };
}

export interface HospitalityAiRecentEntry {
  id: string;
  prompt?: string;
  response?: string;
  reason?: string | null;
  language?: string | null;
  createdAt: string;
  companyId?: string;
  propertyId?: string;
  company?: { id: string; name: string };
  property?: { id: string; address: string; companyId?: string };
  turnId?: string;
}

export interface HospitalityAiRecentList<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  sort: {
    field: string;
    order: 'asc' | 'desc';
  };
}

export interface HospitalityAiMetricsResponse {
  filters: {
    companyId: string | null;
    propertyId: string | null;
    sinceDays: number | null;
  };
  totals: HospitalityAiTotals;
  recent: {
    downvotes: HospitalityAiRecentList<HospitalityAiRecentEntry>;
    turns: HospitalityAiRecentList<HospitalityAiRecentEntry>;
  };
}

export interface HospitalityAiMetricsParams {
  companyId?: string;
  propertyId?: string;
  days?: number;
  downvotesPage?: number;
  downvotesPageSize?: number;
  downvotesSortField?: string;
  downvotesSortOrder?: 'asc' | 'desc';
  turnsPage?: number;
  turnsPageSize?: number;
  turnsSortField?: string;
  turnsSortOrder?: 'asc' | 'desc';
}

export interface HospitalityQaEntryResponse {
  id: string;
  companyId?: string;
  propertyId?: string;
  question: string;
  answer: string;
  language: string | null;
  tags: string[];
  source: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  company?: { id: string; name: string; isActive?: boolean };
  property?: { id: string; address: string; companyId?: string };
}

export interface HospitalityQaListResponse {
  items: HospitalityQaEntryResponse[];
  page: number;
  pageSize: number;
  total: number;
}

export interface HospitalityQaPayload {
  companyId: string;
  propertyId?: string | null;
  language?: string | null;
  question: string;
  answer: string;
  tags?: string[];
  source?: string | null;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HospitalityAiService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  getContext(): Observable<HospitalityAiContext> {
    return this.http
      .get<HospitalityAiContext>(`${BE_API_AUTH_URL}/admin/hospitality-ai/context`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getMetrics(params: HospitalityAiMetricsParams = {}): Observable<HospitalityAiMetricsResponse> {
    let httpParams = new HttpParams();
    if (params.companyId) {
      httpParams = httpParams.set('companyId', params.companyId);
    }
    if (params.propertyId) {
      httpParams = httpParams.set('propertyId', params.propertyId);
    }
    if (typeof params.days === 'number') {
      httpParams = httpParams.set('days', String(params.days));
    }
    if (typeof params.downvotesPage === 'number') {
      httpParams = httpParams.set('downvotesPage', String(params.downvotesPage));
    }
    if (typeof params.downvotesPageSize === 'number') {
      httpParams = httpParams.set('downvotesPageSize', String(params.downvotesPageSize));
    }
    if (params.downvotesSortField) {
      httpParams = httpParams.set('downvotesSortField', params.downvotesSortField);
    }
    if (params.downvotesSortOrder) {
      httpParams = httpParams.set('downvotesSortOrder', params.downvotesSortOrder);
    }
    if (typeof params.turnsPage === 'number') {
      httpParams = httpParams.set('turnsPage', String(params.turnsPage));
    }
    if (typeof params.turnsPageSize === 'number') {
      httpParams = httpParams.set('turnsPageSize', String(params.turnsPageSize));
    }
    if (params.turnsSortField) {
      httpParams = httpParams.set('turnsSortField', params.turnsSortField);
    }
    if (params.turnsSortOrder) {
      httpParams = httpParams.set('turnsSortOrder', params.turnsSortOrder);
    }

    return this.http
      .get<HospitalityAiMetricsResponse>(`${BE_API_AUTH_URL}/admin/hospitality-ai/metrics`, {
        headers: this.authService.createAuthorizationHeader(),
        params: httpParams,
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  listQaEntries(params: {
    companyId?: string;
    propertyId?: string;
    language?: string;
    page?: number;
    pageSize?: number;
    includeInactive?: boolean;
    search?: string;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
  }): Observable<HospitalityQaListResponse> {
    let httpParams = new HttpParams();
    if (params.companyId) {
      httpParams = httpParams.set('companyId', params.companyId);
    }
    if (params.propertyId) {
      httpParams = httpParams.set('propertyId', params.propertyId);
    }
    if (params.language) {
      httpParams = httpParams.set('language', params.language);
    }
    if (typeof params.page === 'number') {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (typeof params.pageSize === 'number') {
      httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    if (params.includeInactive) {
      httpParams = httpParams.set('includeInactive', 'true');
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.sortField) {
      httpParams = httpParams.set('sortField', params.sortField);
    }
    if (params.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<HospitalityQaListResponse>(`${BE_API_AUTH_URL}/admin/hospitality-ai/qa`, {
        headers: this.authService.createAuthorizationHeader(),
        params: httpParams,
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  createQaEntry(payload: HospitalityQaPayload): Observable<HospitalityQaEntryResponse> {
    return this.http
      .post<HospitalityQaEntryResponse>(`${BE_API_AUTH_URL}/admin/hospitality-ai/qa`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateQaEntry(entryId: string, payload: Partial<HospitalityQaPayload>): Observable<HospitalityQaEntryResponse> {
    return this.http
      .put<HospitalityQaEntryResponse>(`${BE_API_AUTH_URL}/admin/hospitality-ai/qa/${entryId}`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteQaEntry(entryId: string, options: { hard?: boolean } = {}): Observable<void> {
    let params = new HttpParams();
    if (options.hard) {
      params = params.set('hard', 'true');
    }
    return this.http
      .delete<void>(`${BE_API_AUTH_URL}/admin/hospitality-ai/qa/${entryId}`, {
        headers: this.authService.createAuthorizationHeader(),
        params,
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
